import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  TemplateRef,
  ViewChild,
  ChangeDetectorRef,
} from "@angular/core";
import { UntypedFormBuilder, FormControl, Validators } from "@angular/forms";
import { Router } from "@angular/router";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Observable, BehaviorSubject } from "rxjs";
import { ApiRequestService } from "@common/services/api-request.service";
import {
  NavigationService,
  SideNavService,
} from "@modules/navigation/services";
import { environment } from "environments/environment";
import { ThemeRuntimeService } from "@app/services/theme-runtime.service";

@Component({
    selector: "sb-layout-auth",
    templateUrl: "./layout-auth.component.html",
    styleUrls: ["layout-auth.component.scss"],
    standalone: false
})
export class LayoutAuthComponent implements OnInit {
  images: string[] = [];
  showNavigationArrows = false;
  showNavigationIndicators = false;
  createAccountBtn = "yes";
  companyCheck = "";
  private organisationLoaded = false;
  private readonly backgroundImagesStorageKeyPrefix = "sv:auth-background-images:v1";

  //smart view host names
  hostname = [
    "localhost",
    "testing-dot-smart-view-utilities-355419.ey.r.appspot.com",
    "dev-dot-smart-view-utilities-355419.ey.r.appspot.com",
    "ums.smartviewtechnology.co.za",
    "ums.smartviewtechnology.co.za",
    "ums-dev.smartviewtechnology.co.za",
    "ums-stage.smartviewtechnology.co.za",
    "ums-testing.smartviewtechnology.co.za",
    "ums.landisgyr.com",
    "ums-landis-dev.smartviewtechnology.co.za",
    "ums-landis-stage.smartviewtechnology.co.za",
    "ums-landis-testing.smartviewtechnology.co.za"
  ];

  constructor(
    private fb: UntypedFormBuilder,
    private changeDetectorRef: ChangeDetectorRef,
    private modalService: NgbModal,
    public navigationService: NavigationService,
    private apiRequest: ApiRequestService,
    private router: Router,
    private http: HttpClient,
    private themeRuntimeService: ThemeRuntimeService
  ) {}

  ngOnInit() {
    this.companyCheck = environment.companyCheck === "eykon"
      ? "eykon"
      : localStorage.getItem("company") ?? environment.companyCheck ?? "default";

    this.images = this.companyCheck === "eykon"
      ? this.getDefaultBackgroundImages()
      : this.loadCachedBackgroundImages() ?? this.getDefaultBackgroundImages();
    this.loadOrganisation();
  }

  loadOrganisation() {
    if (this.organisationLoaded) {
      return;
    }
    this.organisationLoaded = true;

    let devicesBody = {
      payload: {
        domain: window.location.origin,
      },
    };

    this.apiRequest
      .apiRequest("users/organisation/", "organisation.json", devicesBody)
      .subscribe(
        (res) => {
          const organisation = res?.data?.data ?? res?.data;

          if (res.success && organisation) {
            this.themeRuntimeService.applyLoginResponseTheme(organisation, true);
            if (
              this.companyCheck !== "eykon" &&
              Array.isArray(organisation.organisationBackgroundImages) &&
              organisation.organisationBackgroundImages.length > 0
            ) {
              this.setBackgroundImages(organisation.organisationBackgroundImages);
            }
          }
          this.changeDetectorRef.detectChanges();
        },
        (error: any) => {
          this.organisationLoaded = false;
          const defaultOptions = {
            autohide: true,
            delay: 10000,
          };
          localStorage.removeItem("deviceID");
          console.log(error);
          let message = "";
          if (error.error.data) {
            for (var i = 0; i < error.error.data.length; i++) {
              message += error.error.data[i] + "\n";
            }
          }
        }
      );
  }

  private getDefaultBackgroundImages(): string[] {
    if (this.companyCheck === "eykon") {
      return [
        "/assets/eykon-assets/images/eykon-brand-reveal.webp",
        "/assets/eykon-assets/images/eykon-space-brandscape.webp",
        "/assets/eykon-assets/images/eykon-alpine-lake-brandscape.webp",
        "/assets/eykon-assets/images/eykon-cloud-network-cityscape.webp",
      ];
    }

    return this.companyCheck === "landis"
      ? [
          "/assets/landis-assets/images/Landis + Gyr Image 1.png",
          "/assets/landis-assets/images/Landis + Gyr Image 2.png",
          "/assets/landis-assets/images/Landis + Gyr Image 3.png",
          "/assets/landis-assets/images/Landis + Gyr Image 4.png",
        ]
      : [
          "/assets/img/slider/industrial.png",
          "/assets/img/slider/residential.png",
          "/assets/img/slider/retail.png",
          "/assets/img/slider/residential.png",
        ];
  }

  private getBackgroundImagesStorageKey(): string {
    return `${this.backgroundImagesStorageKeyPrefix}:${this.companyCheck}`;
  }

  private loadCachedBackgroundImages(): string[] | null {
    try {
      const raw = localStorage.getItem(this.getBackgroundImagesStorageKey());
      if (!raw) {
        return null;
      }

      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) {
        return null;
      }

      const images = parsed.filter(
        (image): image is string => typeof image === "string" && image.trim().length > 0
      );

      return images.length > 0 ? images : null;
    } catch {
      return null;
    }
  }

  private setBackgroundImages(images: string[]): void {
    const normalizedImages = images.filter(
      (image): image is string => typeof image === "string" && image.trim().length > 0
    );

    if (normalizedImages.length === 0) {
      return;
    }

    this.images = [...normalizedImages];

    try {
      localStorage.setItem(
        this.getBackgroundImagesStorageKey(),
        JSON.stringify(normalizedImages)
      );
    } catch {
      // Ignore storage failures and keep rendering the in-memory set.
    }
  }
}
